import { ISalesRepository } from '@domain/sales/repositories/ISalesRepository';
import { ProductSales } from '@domain/sales/models/Sales';
import { ProductForecasts } from '@domain/sales/models/Forecasts';
import { SalesDataSource } from '../datasources/SalesDataSource';
import { SalesForecastMapper } from '../mappers/SalesForecastMapper';
import { PaginatedResource, Resource } from '@domain/base/Resource';
import { DailySales } from '@domain/sales/models/DailySales';
import { DailySalesMapper } from '../mappers/DailySalesMapper';
import { WeeklySalesMapper } from '../mappers/WeeklySalesMapper';
import { ProductFilter } from '@domain/product/models/ProductFilter';
import { AggregatedQuantity } from '@domain/sales/models/AggregatedQuantity';
import { AggregatedQuantityMapper } from '../mappers/AggregatedQuantityMapper';
import { SalesKpiOptions, SalesKpis } from '@domain/sales/models/SalesKpis';
import { SalesKpisMapper } from '../mappers/SalesKpisMapper';

export class SalesRepository implements ISalesRepository {
  constructor(private dataSource: SalesDataSource) { }

  async syncSales(connectionId: string, date?: Date): Promise<Resource<void>> {
    try {
      await this.dataSource.syncSales(connectionId, date);
      return { status: 'success', data: undefined };
    } catch (error) {
      return {
        status: 'error',
        error: {
          type: 'UnknownError',
          message: 'Failed to sync sales'
        }
      };
    }
  }

  async runForecast(productId?: string, marketplaceConnectionId?: string, backtest?: boolean): Promise<Resource<void>> {
    try {
      await this.dataSource.runForecast(productId, marketplaceConnectionId, backtest);
      return { status: 'success', data: undefined };
    } catch (error) {
      return {
        status: 'error',
        error: {
          type: 'UnknownError',
          message: 'Failed to run forecast'
        }
      };
    }
  }

  async getForecast(
    startDate?: Date,
    endDate?: Date,
    filterCriteria?: ProductFilter,
  ): Promise<PaginatedResource<ProductForecasts[]>> {
    try {
      const response = await this.dataSource.getForecast(startDate, endDate, filterCriteria);
      return {
        ...response,
        status: 'success',
        data: response.data.map(SalesForecastMapper.toModel),
      };
    } catch (error) {
      return {
        status: 'error',
        error: {
          type: 'UnknownError',
          message: 'Failed to fetch forecast'
        }
      };
    }
  }

  async getDailySales(
    productIds?: string[],
    startDate?: Date,
    endDate?: Date,
  ): Promise<Resource<DailySales[]>> {
    try {
      const data = await this.dataSource.getDailySales(productIds, startDate, endDate);
      return { status: 'success', data: data.map(DailySalesMapper.toModel) };
    } catch (error) {
      return {
        status: 'error',
        error: {
          type: 'UnknownError',
          message: 'Failed to fetch daily sales'
        }
      };
    }
  }

  async getWeeklySales(
    startDate?: Date,
    endDate?: Date,
    filterCriteria?: ProductFilter,
    marketplaces?: string[],
    countries?: string[],
  ): Promise<PaginatedResource<ProductSales[]>> {
    try {
      const response = await this.dataSource.getWeeklySales(startDate, endDate, filterCriteria, marketplaces, countries);
      return {
        ...response,
        status: 'success',
        data: response.data.map(WeeklySalesMapper.toModel),
      };
    } catch (error) {
      return {
        status: 'error',
        error: {
          type: 'UnknownError',
          message: 'Failed to fetch weekly sales'
        }
      };
    }
  }

  async getProductSales(
    productId?: string,
    startDate?: Date,
    endDate?: Date,
    marketplaces?: string[],
    countries?: string[],
  ): Promise<Resource<ProductSales>> {
    try {
      const response = await this.dataSource.getProductSales(productId, startDate, endDate, marketplaces, countries);
      return { status: 'success', data: WeeklySalesMapper.toModel(response) };
    } catch (error) {
      return {
        status: 'error',
        error: {
          type: 'UnknownError',
          message: 'Failed to fetch product sales'
        }
      };
    }
  } 

  async getCompanySales(
    startDate?: Date,
    endDate?: Date,
    marketplaces?: string[],
    countries?: string[],
  ): Promise<Resource<AggregatedQuantity[]>> {
    try {
      const response = await this.dataSource.getCompanySales(startDate, endDate, marketplaces, countries);
      return { status: 'success', data: response.map(AggregatedQuantityMapper.toModel) };
    } catch (error) {
      return {
        status: 'error',
        error: {
          type: 'UnknownError',
          message: 'Failed to fetch company sales'
        }
      };
    }
  }

  async getSalesKpis(options?: SalesKpiOptions): Promise<Resource<SalesKpis>> {
    try {
      const response = await this.dataSource.getSalesKpis(options);
      return { status: 'success', data: SalesKpisMapper.toModel(response) };
    } catch (error) {
      return {
        status: 'error',
        error: {
          type: 'UnknownError',
          message: 'Failed to fetch sales KPIs'
        }
      };
    }
  }

  async updateWeeklySalesAnomalies(productId: string, weeks: { week: number, year: number, anomaly: boolean }[]): Promise<Resource<void>> {
    try {
      await this.dataSource.updateWeeklySalesAnomalies(productId, weeks);
      return { status: 'success', data: undefined };
    } catch (error) {
      return { status: 'error', error: { type: 'UnknownError', message: 'Failed to update weekly sales anomalies' } };
    }
  }
  
  async clearWeeklySalesAnomalies(productId: string, weeks: { week: number, year: number }[]): Promise<Resource<void>> {
    try {
      await this.dataSource.clearWeeklySalesAnomalies(productId, weeks);
      return { status: 'success', data: undefined };
    } catch (error) {
      return { status: 'error', error: { type: 'UnknownError', message: 'Failed to clear weekly sales anomalies' } };
    }
  }

  async exportSalesCSV(startDate?: Date, endDate?: Date, marketplaces?: string[], countries?: string[]): Promise<Resource<Blob>> {
    try {
      const blob = await this.dataSource.exportSalesCSV(startDate, endDate, marketplaces, countries);
      return { status: 'success', data: blob };
    } catch (error) {
      return {
        status: 'error',
        error: {
          type: 'UnknownError',
          message: 'Failed to export sales CSV'
        }
      };
    }
  }
}
